import type { TaskComment } from '@frontend/api-types';
import { normalizeForSearch } from '@frontend/duck-tape';
import { useCallback, useEffect, useMemo, useState } from '@frontend/react';

type SearchMatch = {
  commentId: string;
};

type UseConversationSearchResult = {
  clearSearch: () => void;
  currentMatch: SearchMatch | undefined;
  currentMatchIndex: number;
  goToNext: () => void;
  goToPrev: () => void;
  inputQuery: string;
  isSearchActive: boolean;
  matches: SearchMatch[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  totalMatches: number;
};

const HTML_ENTITY_MAP: Record<string, string> = {
  '&amp;': '&',
  '&apos;': "'",
  '&gt;': '>',
  '&lt;': '<',
  '&nbsp;': ' ',
  '&quot;': '"',
};

const decodeHtmlEntities = (text: string): string =>
  text
    .replace(/&(?:amp|apos|gt|lt|nbsp|quot);/g, (entity) => HTML_ENTITY_MAP[entity] ?? entity)
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCharCode(parseInt(hex as string, 16)));

const stripHtmlTags = (html: string): string => decodeHtmlEntities(html.replace(/<[^>]*>/g, ''));

const DEBOUNCE_MS = 100;

/**
 * Hook for searching through conversation messages.
 * Searches the plain-text content of comment bodies (stripping HTML/markdown tags).
 * Returns matches with navigation (next/prev) through results.
 *
 * The search input is debounced (100ms) internally — use `inputQuery` for the
 * search bar display value and `searchQuery` (debounced) for highlighting text.
 */
export const useConversationSearch = (comments: TaskComment[]): UseConversationSearchResult => {
  const [inputQuery, setInputQueryRaw] = useState('');
  const [searchQuery, setSearchQueryDebounced] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQueryDebounced(inputQuery);
      setCurrentMatchIndex(0);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputQuery]);

  const matches = useMemo(() => {
    const trimmed = normalizeForSearch(searchQuery.trim()).toLowerCase();
    if (!trimmed) return [];

    return comments.reduce<SearchMatch[]>((acc, comment) => {
      const plainText = normalizeForSearch(stripHtmlTags(comment.body)).toLowerCase();
      if (plainText.includes(trimmed)) {
        return [...acc, { commentId: comment.id }];
      }
      return acc;
    }, []);
  }, [comments, searchQuery]);

  const totalMatches = matches.length;
  const currentMatch = matches[currentMatchIndex];

  const goToNext = useCallback(() => {
    setCurrentMatchIndex((prev) => (totalMatches > 0 ? (prev + 1) % totalMatches : 0));
  }, [totalMatches]);

  const goToPrev = useCallback(() => {
    setCurrentMatchIndex((prev) => (totalMatches > 0 ? (prev - 1 + totalMatches) % totalMatches : 0));
  }, [totalMatches]);

  const setSearchQuery = useCallback((query: string) => {
    setInputQueryRaw(query);
  }, []);

  const clearSearch = useCallback(() => {
    setInputQueryRaw('');
    setSearchQueryDebounced('');
    setCurrentMatchIndex(0);
  }, []);

  return {
    clearSearch,
    currentMatch,
    currentMatchIndex,
    goToNext,
    goToPrev,
    inputQuery,
    isSearchActive: inputQuery.trim().length > 0,
    matches,
    searchQuery,
    setSearchQuery,
    totalMatches,
  };
};
